import React from 'react';
import { getImageUrl } from '../../../utils/api';
import { getEmbedUrl, isDirectVideoFile, isDummyUrl } from '../../../utils/mediaUtils';

interface VirtualTour {
    title: string;
    link: string;
}

interface MediaTourProps {
    project: any;
}

const MediaTour: React.FC<MediaTourProps> = ({ project }) => {
    const [activeTourIndex, setActiveTourIndex] = React.useState(0);
    const [activeVideoIndex, setActiveVideoIndex] = React.useState(0);

    // Normalize videos to a single array format, filtering out dummy/empty links
    const videos: { title: string; link: string }[] = [];
    if (Array.isArray(project.youtubeVideos) && project.youtubeVideos.length > 0) {
        project.youtubeVideos.forEach((v: any, idx: number) => {
            if (v && v.link && !isDummyUrl(v.link)) {
                videos.push({ title: v.title || `Video ${idx + 1}`, link: v.link });
            }
        });
    }
    if (project.videoUrl && !isDummyUrl(project.videoUrl) && !videos.some(v => v.link === project.videoUrl)) {
        videos.push({ title: 'Project Walkthrough', link: project.videoUrl });
    }
    if (project.projectVideoUrl && !isDummyUrl(project.projectVideoUrl) && !videos.some(v => v.link === project.projectVideoUrl)) {
        videos.push({ title: 'Featured Video', link: project.projectVideoUrl });
    }

    // Normalize tours to a single array format, filtering out dummy/empty links
    const tours: VirtualTour[] = [];
    if (Array.isArray(project.virtualTours) && project.virtualTours.length > 0) {
        project.virtualTours.forEach((t: any, idx: number) => {
            if (t && t.link && !isDummyUrl(t.link)) {
                tours.push({ title: t.title || `360° Tour ${idx + 1}`, link: t.link });
            }
        });
    }
    if (project.virtualTour360 && !isDummyUrl(project.virtualTour360) && !tours.some(t => t.link === project.virtualTour360)) {
        tours.push({ title: '360° Virtual Tour', link: project.virtualTour360 });
    }
    if (project.virtualTourLink && !isDummyUrl(project.virtualTourLink) && !tours.some(t => t.link === project.virtualTourLink)) {
        tours.push({ title: 'Virtual Tour', link: project.virtualTourLink });
    }
    const mapGoogle3D = project.google3dViewLink || project.mapDetails?.google3dViewLink;
    if (mapGoogle3D && !isDummyUrl(mapGoogle3D) && !tours.some(t => t.link === mapGoogle3D)) {
        tours.push({ title: '3D Satellite View', link: mapGoogle3D });
    }

    const hasVideos = videos.length > 0;
    const hasTours = tours.length > 0;

    if (!hasVideos && !hasTours) return null;

    const currentVideo = videos[activeVideoIndex];
    const isDirectVideo = currentVideo ? isDirectVideoFile(currentVideo.link) : false;
    const embedVideoUrl = currentVideo ? getEmbedUrl(currentVideo.link) : '';

    const currentTour = tours[activeTourIndex];
    const isDirectTourVideo = currentTour ? isDirectVideoFile(currentTour.link) : false;
    const embedTourUrl = currentTour ? getEmbedUrl(currentTour.link) : '';

    return (
        <div id="media-tour" className="space-y-6">
            {hasVideos && (
                <div id="project-video" className="card border border-gray-200 shadow-sm rounded-2xl overflow-hidden bg-white">
                    <div className="py-4 px-6 md:px-8 border-b border-gray-100">
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center text-rose-600">
                                    <i className="fa-brands fa-youtube text-lg"></i>
                                </div>
                                <h4 className="text-xl md:text-2xl mb-0 font-bold text-black">Project Videos</h4>
                            </div>
                            
                            {/* Video Tabs */}
                            {videos.length > 1 && (
                                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                                    {videos.map((vid: any, idx: number) => (
                                        <button
                                            key={idx}
                                            onClick={() => setActiveVideoIndex(idx)}
                                            className={`px-4 py-2 rounded-lg text-[13px] md:text-[14px] font-black transition-all whitespace-nowrap border-2 ${
                                                activeVideoIndex === idx 
                                                ? 'bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] border-[#f1ae24] shadow-lg scale-105 ring-1 ring-[#f1ae24]/20' 
                                                : 'bg-white text-[#05144b] border-gray-100 hover:border-[#05144b]/20 opacity-70 hover:opacity-100'
                                            }`}
                                        >
                                            {vid.title || `Video ${idx + 1}`}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="p-0">
                        <div className="aspect-video w-full h-[400px] md:h-[550px] bg-black">
                            {currentVideo && (
                                isDirectVideo ? (
                                    <video
                                        src={getImageUrl(currentVideo.link)}
                                        controls
                                        playsInline
                                        className="w-full h-full object-cover"
                                    ></video>
                                ) : embedVideoUrl ? (
                                    <iframe
                                        src={embedVideoUrl}
                                        title={currentVideo.title || "Project Video"}
                                        allowFullScreen
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        className="w-full h-full border-0"
                                    ></iframe>
                                ) : (
                                    <div className="w-full h-full flex flex-col items-center justify-center text-white/70 p-6 text-center">
                                        <i className="fa-solid fa-film text-3xl mb-2 text-[#f1ae24]"></i>
                                        <p className="text-sm font-semibold">Video preview will be available soon.</p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                    {/* Caption for mobile if many videos */}
                    {videos.length > 1 && (
                        <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 md:hidden">
                            <p className="text-[12px] font-medium text-black text-center">
                                <i className="fa-solid fa-hand-pointer mr-2"></i> Select a video from the tabs above
                            </p>
                        </div>
                    )}
                </div>
            )}

            {hasTours && (
                <div id="project-360-tour" className="card border border-gray-200 shadow-sm rounded-2xl overflow-hidden bg-white">
                    <div className="py-4 px-6 md:px-8 border-b border-gray-100">
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                                    <i className="fa-solid fa-glasses text-lg"></i>
                                </div>
                                <h4 className="text-xl md:text-2xl mb-0 font-bold text-black">360° Virtual Tour</h4>
                            </div>
                            
                            {/* Tour Tabs */}
                            {tours.length > 1 && (
                                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                                    {tours.map((tour: VirtualTour, idx: number) => (
                                        <button
                                            key={idx}
                                            onClick={() => setActiveTourIndex(idx)}
                                            className={`px-4 py-2 rounded-lg text-[13px] md:text-[14px] font-black transition-all whitespace-nowrap border-2 ${
                                                activeTourIndex === idx 
                                                ? 'bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] border-[#f1ae24] shadow-lg scale-105 ring-1 ring-[#f1ae24]/20' 
                                                : 'bg-white text-[#05144b] border-gray-100 hover:border-[#05144b]/20 opacity-70 hover:opacity-100'
                                            }`}
                                        >
                                            {tour.title || `Tour ${idx + 1}`}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="p-0">
                        <div className="aspect-video w-full h-[400px] md:h-[550px] bg-black">
                            {currentTour && (
                                isDirectTourVideo ? (
                                    <video
                                        src={getImageUrl(currentTour.link)}
                                        controls
                                        playsInline
                                        className="w-full h-full object-cover"
                                    ></video>
                                ) : embedTourUrl ? (
                                    <iframe
                                        src={embedTourUrl}
                                        title={currentTour.title || "360 Tour"}
                                        allowFullScreen
                                        allow="xr-spatial-tracking; gyroscope; accelerometer; fullscreen"
                                        className="w-full h-full border-0"
                                    ></iframe>
                                ) : (
                                    <div className="w-full h-full flex flex-col items-center justify-center text-white/70 p-6 text-center">
                                        <i className="fa-solid fa-vr-cardboard text-3xl mb-2 text-[#f1ae24]"></i>
                                        <p className="text-sm font-semibold">Virtual tour is currently being processed.</p>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                    {/* Caption for mobile if many tours */}
                    {tours.length > 1 && (
                        <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 md:hidden">
                            <p className="text-[12px] font-medium text-black text-center">
                                <i className="fa-solid fa-hand-pointer mr-2"></i> Select a tour from the tabs above
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MediaTour;


